import React from "react";
import { Link, useLocation } from "react-router-dom";
import { Bell } from "lucide-react";
import {
  useNotificationsContext,
  useNotificationsList,
  type Notification,
} from "../../contexts/NotificationsContext";
import {
  NotificationIcon,
  formatNotificationDate,
  linkLikeProps,
  notificationDestination,
  notificationTicketLabel,
  useOpenNotification,
} from "./notificationDisplay";

// Cuántas muestra el desplegable; el resto, en la página de Notificaciones.
const PREVIEW_COUNT = 8;

interface NotificationBellProps {
  /** Hacia dónde se abre el desplegable: a la derecha de la campanita o a la izquierda. */
  align?: "left" | "right";
}

// Campanita de la barra: contador de no leídas y desplegable con las últimas.
// Tocar una lleva al ticket o recurso que la generó. El contador sale del
// resumen; la lista se pide al abrir el desplegable.
const NotificationBell: React.FC<NotificationBellProps> = ({
  align = "right",
}) => {
  const { notifications, unreadCount, isLoading, markAllAsRead } =
    useNotificationsContext();
  const openNotification = useOpenNotification();
  const location = useLocation();
  const [open, setOpen] = React.useState(false);
  const rootRef = React.useRef<HTMLDivElement>(null);
  useNotificationsList(open);

  // Cerrar al cambiar de ruta, al tocar afuera o con Escape.
  React.useEffect(() => {
    setOpen(false);
  }, [location.pathname, location.search, location.hash]);

  React.useEffect(() => {
    if (!open) return;
    const onPointer = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onPointer);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onPointer);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const latest = notifications.slice(0, PREVIEW_COUNT);
  const label =
    unreadCount > 0
      ? `Notificaciones (${unreadCount} sin leer)`
      : "Notificaciones";

  const open_ = (n: Notification) => {
    setOpen(false);
    openNotification(n);
  };

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        className="relative inline-flex h-8 w-8 items-center justify-center rounded-md border border-border text-foreground hover:bg-muted transition-colors"
        aria-label={label}
        title={label}
        aria-haspopup="true"
        aria-expanded={open}
      >
        <Bell size={16} />
        {unreadCount > 0 && (
          <span className="absolute -top-1.5 -right-1.5 inline-flex min-w-[1.125rem] h-[1.125rem] items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-medium leading-none text-white">
            {unreadCount > 9 ? "9+" : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div
          className={`absolute top-full mt-2 z-50 w-[min(22rem,calc(100vw-2rem))] overflow-hidden rounded-md border border-border bg-popover text-popover-foreground shadow-lg ${align === "right" ? "right-0" : "left-0"}`}
          role="dialog"
          aria-label="Últimas notificaciones"
        >
          <div className="flex items-center justify-between gap-2 border-b border-border px-3 py-2">
            <span className="text-sm font-medium">Notificaciones</span>
            {unreadCount > 0 && (
              <button
                type="button"
                onClick={() => void markAllAsRead()}
                className="text-xs text-primary hover:underline"
              >
                Marcar todas como leídas
              </button>
            )}
          </div>

          {latest.length === 0 ? (
            <p className="px-3 py-6 text-center text-sm text-muted-foreground">
              {isLoading ? "Cargando…" : "No hay notificaciones."}
            </p>
          ) : (
            <ul className="max-h-[60vh] overflow-y-auto divide-y divide-border">
              {latest.map((n) => {
                const destination = notificationDestination(n);
                const ticketLabel = notificationTicketLabel(n);
                return (
                  <li key={n.id}>
                    <div
                      {...linkLikeProps(() => open_(n))}
                      {...(destination ? {} : { role: "button" as const })}
                      className={`flex gap-2.5 px-3 py-2.5 text-left cursor-pointer hover:bg-muted focus-visible:outline-none focus-visible:bg-muted ${n.read ? "" : "bg-blue-50/70 dark:bg-blue-900/20"}`}
                    >
                      <span className="mt-0.5">
                        <NotificationIcon type={n.type} />
                      </span>
                      <span className="min-w-0 flex-1">
                        <span
                          className={`block truncate text-[13px] ${n.read ? "text-foreground" : "font-medium text-foreground"}`}
                        >
                          {n.title}
                        </span>
                        <span className="block text-xs text-muted-foreground line-clamp-2">
                          {n.message}
                        </span>
                        <span className="mt-0.5 block text-[11px] text-muted-foreground">
                          {formatNotificationDate(n.createdAt)}
                          {ticketLabel ? ` · ${ticketLabel}` : ""}
                          {n.targetMissing ? " · El ticket ya no existe" : ""}
                        </span>
                      </span>
                      {!n.read && (
                        <span
                          className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-blue-500"
                          aria-label="Sin leer"
                        />
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}

          <Link
            to="/notifications"
            className="block border-t border-border px-3 py-2 text-center text-sm text-primary hover:bg-muted"
          >
            Ver todas
          </Link>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
